import type { CSSProperties } from 'react';

// Editorial design tokens — shared across all redesigned pages
export const E = {
  ink: '#18191B',
  inkSoft: '#2a2b2e',
  cream: '#f5f2ed',
  cream2: '#ece7dc',
  gold: '#daba7b',
  goldDeep: '#b9a273',
  muted: '#6b6a64',
  rule: 'rgba(24,25,27,0.18)',
  ruleSoft: 'rgba(24,25,27,0.08)',

  // Font stacks
  serif: "'Forum', serif",
  sans: "var(--font-jakarta), 'Inter', system-ui, sans-serif",

  // Common wrappers
  page: {
    background: '#f5f2ed',
    color: '#18191B',
    fontFamily: "var(--font-jakarta), 'Inter', system-ui, sans-serif",
    minHeight: 'calc(100vh - 80px)',
  } as CSSProperties,

  sectionPad: '80px 56px 48px' as const,
  sectionPadSm: '56px 56px 32px' as const,

  // Section label (e.g. "① · OUR SERVICES")
  label: {
    fontFamily: "var(--font-jakarta), 'Inter', system-ui, sans-serif",
    fontSize: 11,
    letterSpacing: '0.32em',
    textTransform: 'uppercase' as const,
    color: '#b9a273',
    marginBottom: 12,
  } as CSSProperties,

  // Large italic serif heading
  h1: {
    fontFamily: "'Forum', serif",
    fontSize: 'clamp(40px, 5vw, 68px)',
    fontWeight: 400,
    fontStyle: 'italic',
    margin: 0,
    lineHeight: 1.05,
  } as CSSProperties,

  h2: {
    fontFamily: "'Forum', serif",
    fontSize: 'clamp(32px, 3.5vw, 52px)',
    fontWeight: 400,
    fontStyle: 'italic',
    margin: 0,
  } as CSSProperties,

  h3: {
    fontFamily: "'Forum', serif",
    fontSize: 24,
    fontWeight: 400,
    fontStyle: 'italic',
    margin: 0,
  } as CSSProperties,

  body: {
    fontFamily: "var(--font-jakarta), 'Inter', system-ui, sans-serif",
    fontSize: 15,
    lineHeight: 1.7,
    color: '#2a2b2e',
  } as CSSProperties,

  // Uppercase sans label
  cap: {
    fontFamily: "var(--font-jakarta), 'Inter', system-ui, sans-serif",
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase' as const,
  } as CSSProperties,

  // Input style
  input: {
    fontFamily: "var(--font-jakarta), 'Inter', system-ui, sans-serif",
    fontSize: 15,
    border: 'none',
    borderBottom: '1px solid rgba(24,25,27,0.18)',
    background: 'transparent',
    outline: 'none',
    color: '#18191B',
    width: '100%',
    padding: '10px 0',
    borderRadius: 0,
  } as CSSProperties,

  // Primary button
  btn: {
    fontFamily: "var(--font-jakarta), 'Inter', system-ui, sans-serif",
    background: '#18191B',
    color: '#f5f2ed',
    border: 'none',
    padding: '16px 32px',
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
  } as CSSProperties,

  // Ghost button
  btnGhost: {
    fontFamily: "var(--font-jakarta), 'Inter', system-ui, sans-serif",
    background: 'transparent',
    color: '#18191B',
    border: '1px solid rgba(24,25,27,0.4)',
    padding: '14px 28px',
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
  } as CSSProperties,

  // Gold button
  btnGold: {
    fontFamily: "var(--font-jakarta), 'Inter', system-ui, sans-serif",
    background: '#daba7b',
    color: '#18191B',
    border: 'none',
    padding: '16px 32px',
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
  } as CSSProperties,
};

/** Link / button alignment for editorial error, 404, and deployment screens */
export const editorialBtnInlineBase: CSSProperties = {
  borderRadius: 0,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/** Rule + flex row for primary / ghost actions */
export const editorialErrorActionsRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  paddingTop: 8,
  borderTop: `1px solid ${E.rule}`,
  alignItems: 'center',
};

/** Dark band (FROM THE EDITOR + footer) — CSS vars keep surfaces identical */
export const editorBandSurface: CSSProperties = {
  background: 'var(--nl-editor-band-bg)',
  backgroundColor: 'var(--nl-editor-band-bg)',
  color: 'var(--nl-editor-band-fg)',
};
